import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";

/* Stylesheet */
import styles from "./ChartTooltip.module.scss";

/* Interfaces */
import {
  ChartTooltipProps,
  ContentProps,
} from "src/shared/interfaces/ChartTooltip";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";

const ChartTooltip = forwardRef((_, ref) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const scrollState = useSelector(
    (state: RootState) => state.scroll
  );

  /**
   * scrollState is used to hide tooltip when user is scrolling.
   * Otherwise, tooltip stays on last hovered position.
   * Scrolling is detected in InsightsPageWrapper component.
   */
  useEffect(() => {
    if (scrollState.isScrolling) {
      setTooltipProps((prev) => ({ ...prev, show: false }));
    }
  }, [scrollState.isScrolling]);

  const [tooltipProps, setTooltipProps] =
    useState<ChartTooltipProps>({
      show: false,
      content: [],
      x: 0,
      y: 0,
      position: "bottom",
    });

  /**
   * forwardRef() is used to forward ref to the parent component.
   * With useImperativeHandle() functions within child component
   * can be called from parent component.
   */
  useImperativeHandle(ref, () => ({
    showTooltip: (
      content: ContentProps[],
      x: number,
      y: number
    ) => {
      const reachedTop = y < 21;
      const adjustedY = reachedTop ? y + 21 : y;

      setTooltipProps({
        show: true,
        content,
        x,
        y: adjustedY,
        position: reachedTop ? "top" : "bottom",
      });
    },
    hideTooltip: () => {
      setTooltipProps((prev) => ({ ...prev, show: false }));
    },
  }));

  if (!tooltipProps.show) return null;

  return (
    <div
      ref={tooltipRef}
      className={`${styles.chart_tooltip} ${
        tooltipProps.position === "top" ? styles.top : ""
      }`}
      style={{
        top: tooltipProps.y,
        left: tooltipProps.x,
        opacity: tooltipProps.show ? 1 : 0,
        pointerEvents: "none",
      }}
    >
      {tooltipProps.content.map(
        ({ key, value, color }, index) => (
          <div
            key={index}
            className={styles.content_container}
          >
            {color ? (
              <div
                style={{ background: color }}
                className={styles.color}
              />
            ) : undefined}
            {key && (
              <div className={styles.key}>{key}:</div>
            )}
            <div className={styles.value}>{value}</div>
          </div>
        )
      )}
    </div>
  );
});

ChartTooltip.displayName = "ChartTooltip";

export default ChartTooltip;
